import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { normalize } from 'normalizr';
import {
  undergradCycles,
  undergradDisciplines,
  undergradLessons,
  undergradLives,
} from '@/store/undergrad/undergrad_api_normalizr_schema';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '@/i18n';
import { UndergradDisciplineState } from './state';
import { DisciplinesInstancesReaderService } from '@/services/undergrad/disciplines-reader.service';
import SentryClient from '@/helpers/sentry';
import { GetDisciplinesInstancesLivesForHomeResult } from '@/models/undergrad/services/disciplines-reader.model';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { StellateDisciplinesInstancesReaderService } from '@/services/undergrad/stellate/stellate-disciplines-instances-reader.service';
import { UndergradLive } from '@/models/undergrad/undergrad-live.model';

const actions: ActionTree<UndergradDisciplineState, RootState> = {
  async PULL_USER_DISCIPLINES_AND_CYCLES({ commit, dispatch }, courseId: string) {
    try {
      const { disciplinesData, cyclesData } =
        await DisciplinesInstancesReaderService.getDisciplinesAndCyclesByCourse(courseId);

      const disciplinesIds = disciplinesData.map(({ id, disciplineInstanceId, isMigrated }) => {
        return { disciplineId: id, disciplineInstanceId: isMigrated ? null : disciplineInstanceId };
      });

      dispatch('PULL_FORUM_API_CHANNELS', { courseId, disciplinesIds });

      const normalizedDisciplines = normalize(disciplinesData, [undergradDisciplines]);
      const normalizedCycle = normalize(cyclesData, [undergradCycles]);

      commit('SET_CYCLES', normalizedCycle.entities);
      commit('SET_DISCIPLINES', normalizedDisciplines.entities);

      if (normalizedCycle.entities.undergradCycles) {
        const lastCycle = Object.values(normalizedCycle.entities.undergradCycles).at(-1);

        commit('SET_CURRENT_CYCLE', lastCycle);
        commit('SET_CURRENT_DISCIPLINES', lastCycle.disciplineIds);
      }
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleRetrieveDisciplines').toString(),
      });
    }
  },
  async PULL_DISCIPLINE_INSTANCE_INFO(
    { commit, dispatch },
    payload: { courseId: string; disciplineId: string; userId: number },
  ) {
    const { courseId, disciplineId, userId } = payload;

    try {
      commit('SET_LOADING_DISCIPLINE_DATA', { disciplineId, isLoading: true });

      const disciplineInstance = await DisciplinesInstancesReaderService.getDisciplineInstanceInfo(
        courseId,
        disciplineId,
      );

      const normalizedLessons = normalize(disciplineInstance.lessons, [undergradLessons]);
      commit('SET_LESSONS', normalizedLessons.entities);

      commit('SET_DISCIPLINE_LESSONS', {
        disciplineId,
        entities: normalizedLessons.entities,
      });

      await dispatch('PULL_REVISION_LISTS', {
        userId,
        disciplineId,
        reviewListsIds: disciplineInstance.reviewLists.map((rl) =>
          parseInt(rl.dexReviewListId, 10),
        ),
      });

      return true;
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleRetrieveDisciplines').toString(),
      });

      return false;
    } finally {
      commit('SET_LOADING_DISCIPLINE_DATA', { disciplineId, isLoading: false });
    }
  },
  async PULL_PREVIEW_DISCIPLINE_INSTANCE_INFO({ commit }, payload: { disciplineSlug: string }) {
    const { disciplineSlug } = payload;

    try {
      const disciplineInstance =
        await StellateDisciplinesInstancesReaderService.getPreviewDisciplineInstance(
          disciplineSlug,
        );

      const normalizedDisciplines = normalize([disciplineInstance], [undergradDisciplines]);

      commit('SET_DISCIPLINES', normalizedDisciplines.entities);

      const normalizedLessons = normalize(disciplineInstance.lessons, [undergradLessons]);
      commit('SET_LESSONS', normalizedLessons.entities);

      commit('SET_DISCIPLINE_LESSONS', {
        disciplineId: disciplineInstance.id,
        entities: normalizedLessons.entities,
      });

      return disciplineInstance;
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleRetrieveDisciplines').toString(),
      });

      return false;
    }
  },
  async PULL_DISCIPLINE_INSTANCE_LIVES(
    { commit },
    { id: disciplineId, disciplineInstanceId }: UndergradDiscipline,
  ) {
    try {
      commit('SET_LOADING_DISCIPLINE_LIVES', { disciplineId, isLoading: true });

      if (disciplineInstanceId) {
        const disciplineLives = await DisciplinesInstancesReaderService.getDisciplineInstanceLives(
          disciplineInstanceId,
        );

        const normalizedLives = await normalize(disciplineLives, [undergradLives]);

        commit('SET_DISCIPLINE_LIVES', { disciplineId, entities: normalizedLives.entities });
      }
    } catch (error) {
      SentryClient.captureError(error);
    } finally {
      commit('SET_LOADING_DISCIPLINE_LIVES', { disciplineId, isLoading: false });
    }
  },
  async PULL_DISCIPLINES_INSTANCES_LIVES({ commit, getters }) {
    const disciplines: UndergradDiscipline[] = getters.undergradDisciplines;
    const disciplinesIds: string[] = disciplines.map((d: UndergradDiscipline) => d.id);

    try {
      commit('SET_LOADING_HOME_DISCIPLINES_LIVES', { disciplinesIds, isLoading: true });

      const disciplinesInstances =
        await StellateDisciplinesInstancesReaderService.getDisciplinesInstancesLivesForHome(
          disciplines
            .filter((d) => d.disciplineInstanceId)
            .map((d) => d.disciplineInstanceId as string),
        );

      disciplinesInstances.forEach(
        (disciplineInstance: GetDisciplinesInstancesLivesForHomeResult) => {
          const disciplineLives = disciplineInstance.faculLiveVideo.map((live: UndergradLive) => ({
            ...live,
            paramsSlug: disciplineInstance.faculDiscipline.slug,
          }));

          const normalizedResponse = normalize(disciplineLives, [undergradLives]);

          commit('SET_DISCIPLINE_LIVES', {
            disciplineId: disciplineInstance.faculDiscipline.id,
            entities: normalizedResponse.entities,
          });
        },
      );
    } catch (error) {
      SentryClient.captureError(error);
    } finally {
      commit('SET_LOADING_HOME_DISCIPLINES_LIVES', { disciplinesIds, isLoading: false });
    }
  },
};

export default actions;
